<script lang="ts">
  import type { AccessCode, AccessCodeMetadata } from '@gat-ship/types'
  import {
    IconMoreHorizontal,
    IconChevronDown,
    type IconComponent
  } from '@gat-ship/icons'
  import type { HTMLAttributes } from 'vue'

  export type AccessRolePermissionGroupProps = {
    class?: HTMLAttributes['class']
    groupName: string
    icon: IconComponent
    description: string
    permissionItems: AccessCode[]
    isCollapsible?: boolean
  }
</script>

<script setup lang="ts">
  import { usePermissionsView } from '../../composables/usePermissionsView'
  import { usePermissionDecisions } from '../../composables/usePermissionDecisions'
  import { computed, ref } from 'vue'
  import { useAccessRoleDetailProvider } from '../providers/access-role-detail-context'
  import { provideAccessRolePermissionGroupContext } from './access-role-permission-group-context'
  import AccessRolePermissionItem from './AccessRolePermissionItem.vue'
  import {
    permissionGroups,
    accessCodeMetadata
  } from '../../access-control-text'
  import { Switch, TooltipHint } from '@gat-ship/vue-components'
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'

  const props = defineProps<AccessRolePermissionGroupProps>()

  const groupPermissionItems: (AccessCodeMetadata & {
    accessCode: AccessCode
  })[] =
    permissionGroups[props.groupName]?.permissionItems.map((item) => ({
      ...accessCodeMetadata[item as AccessCode],
      accessCode: item
    })) ?? []

  const accessRoleProvider = useAccessRoleDetailProvider()

  const decisions = usePermissionDecisions()

  const permissionCodes = computed(
    () => accessRoleProvider.dataSource.accessRole.value?.permissionCodes ?? []
  )

  const computedGroupPermissionItems = computed(() =>
    decisions.visibleGroupItems(groupPermissionItems, permissionCodes.value)
  )

  const searchResultPermissionItems = ref<string[]>([])

  function addPermissionItemToSearchResult(id: string) {
    if (!searchResultPermissionItems.value.includes(id)) {
      searchResultPermissionItems.value.push(id)
    }
  }
  function removePermissionItemFromSearchResult(id: string) {
    searchResultPermissionItems.value =
      searchResultPermissionItems.value.filter((item) => item !== id)
  }

  const { groupName } = provideAccessRolePermissionGroupContext({
    groupName: props.groupName,
    addPermissionItemToSearchResult,
    removePermissionItemFromSearchResult
  })

  const { localStorageState, search } = usePermissionsView()

  function hasSearchMatch(value: string) {
    if (!value) {
      return true
    }
    const isVisible = searchResultPermissionItems.value.length > 0
    return isVisible
  }

  function onEnableAllClicked() {
    groupPermissionItems.forEach((item) => {
      accessRoleProvider.dataSource.addRolePermissions([item.accessCode])
    })
  }

  function isAllEnabled() {
    return decisions.allGroupGranted(
      groupPermissionItems,
      permissionCodes.value
    )
  }

  function isAllDisabled() {
    return decisions.allGroupRevoked(
      groupPermissionItems,
      permissionCodes.value
    )
  }

  function onDisableAllClicked() {
    groupPermissionItems.forEach((item) => {
      accessRoleProvider.dataSource.removeRolePermissions([item.accessCode])
    })
  }

  const grantedPermissionItems = computed(() =>
    decisions.grantedGroupItems(
      computedGroupPermissionItems.value,
      permissionCodes.value
    )
  )

  const hiddenPermissionItems = computed(() =>
    decisions.hiddenGroupItems(
      groupPermissionItems,
      permissionCodes.value,
      localStorageState.hideRequiredPermissions.value
    )
  )
</script>

<template>
  <div
    :class="[{ hidden: !hasSearchMatch(search) }, props.class]"
    :data-hidden="!hasSearchMatch(search)"
  >
    <div class="group flex select-none flex-col">
      <div
        class="flex h-12 grow items-center gap-4"
        :class="{
          'cursor-pointer group-hover:text-neutral-700 dark:group-hover:text-neutral-100':
            props.isCollapsible
        }"
      >
        <component
          :is="icon"
          class="lucide-icon size-6 shrink-0"
          :stroke-width="1.5"
        />
        <div class="flex flex-col gap-2">
          <div class="flex items-end gap-1 self-center">
            <div class="text-base font-semibold leading-none tracking-wide">
              {{ groupName }}
            </div>

            <TooltipHint v-if="description" :text="description" />
          </div>
          <div class="flex items-center gap-2 sm:hidden">
            <div
              v-if="hiddenPermissionItems.length > 0"
              class="bg-primary/10 text-primary flex rounded-full px-2 py-0.5 text-xs leading-none sm:hidden"
            >
              {{ hiddenPermissionItems.length }} hidden
            </div>
          </div>
        </div>
        <div v-if="!search" class="ml-auto flex items-center gap-2">
          <TooltipHint
            v-if="hiddenPermissionItems.length > 0"
            :text="
              hiddenPermissionItems
                .map((item) => accessCodeMetadata[item].title)
                .join('\r\n')
            "
          >
            <div
              class="bg-primary/10 text-primary hidden rounded-full px-2 py-0.5 text-xs leading-none sm:block"
            >
              {{ hiddenPermissionItems.length }} hidden
            </div>
          </TooltipHint>
          <div class="text-muted-foreground text-xs leading-none">
            {{ grantedPermissionItems.length }} /
            {{ computedGroupPermissionItems.length }}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <div
                class="more-button group/more flex size-8 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                @click.stop
              >
                <icon-more-horizontal class="size-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                v-if="!isAllEnabled()"
                @click="onEnableAllClicked"
              >
                <span>Enable All</span>
                <Switch
                  :model-value="true"
                  class="pointer-events-none data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-neutral-300 dark:data-[state=checked]:bg-green-600 dark:data-[state=unchecked]:bg-neutral-700"
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="!isAllDisabled()"
                @click="onDisableAllClicked"
              >
                <span>Disable All</span>
                <Switch
                  :model-value="false"
                  class="pointer-events-none data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-neutral-300 dark:data-[state=checked]:bg-green-600 dark:data-[state=unchecked]:bg-neutral-700"
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div
            v-if="props.isCollapsible"
            class="flex size-8 items-center justify-center rounded-full bg-neutral-100 transition-all duration-150 group-hover:bg-neutral-200 group-has-[.more-button:hover]:bg-neutral-100 dark:bg-neutral-800 dark:group-hover:bg-neutral-700 dark:group-has-[.more-button:hover]:bg-neutral-800"
          >
            <icon-chevron-down
              class="size-4"
              :class="{
                'rotate-180':
                  localStorageState.collapsedGroups.value.includes(groupName)
              }"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="-mb-[2px] divide-y divide-neutral-200 overflow-hidden rounded-md border border-neutral-100 bg-neutral-50 px-4 dark:divide-neutral-700 dark:border-neutral-900 dark:bg-neutral-800"
    >
      <AccessRolePermissionItem
        v-for="permissionItem in permissionItems"
        :key="permissionItem"
        :access-code="permissionItem"
      />
    </div>
  </div>
</template>
